/**
 * Structural fields used to classify an attachment/media payload as a
 * recorded voice note vs an uploaded audio file. Domain layers map their
 * types onto this shape and call the helpers below.
 */
import type { VoiceNoteShape } from '../types/voice-note-shape.types'

export type { VoiceNoteShape } from '../types/voice-note-shape.types'

/**
 * Canonical clip length: posts/comments persist `duration`; chats/backend may send `durationSec`.
 */
export function resolveVoiceDuration(media: Pick<VoiceNoteShape, 'duration' | 'durationSec'> | null | undefined): number | undefined {
  return media?.duration ?? media?.durationSec ?? undefined
}

function isAudioPayload(media: VoiceNoteShape): boolean {
  const type = media.type?.toLowerCase() ?? ''
  return type === 'audio' || type.startsWith('audio/')
}

/**
 * Explicit uploaded audio file (file-picker / drag-drop), rendered with the
 * shared voice playbox (timeline by default; waveform only if peaks exist and
 * the uploaded-audio config is set to waveform).
 */
export function isUploadedAudioShape(media: VoiceNoteShape | null | undefined): boolean {
  if (!media || !isAudioPayload(media)) return false
  return media.kind === 'attachment'
}

/**
 * Whether a media/attachment payload is a recorded voice note.
 * Mirrors backend/Flutter classification: audio that carries an explicit
 * `kind: 'voice_note'`, waveform data, or a DRM license with a positive duration.
 * Explicit `kind: 'attachment'` always wins (uploaded audio is never a voice note).
 */
export function isVoiceNoteShape(media: VoiceNoteShape | null | undefined): boolean {
  if (!media || !isAudioPayload(media)) return false
  if (media.kind === 'attachment') return false
  const duration = resolveVoiceDuration(media) ?? 0
  return media.kind === 'voice_note' || (!!media.licenseId && duration > 0) || (media.waveformPeaks?.length ?? 0) > 0
}

/**
 * Audio that should render an inline player (waveform voice note OR timeline upload),
 * rather than a generic file attachment card.
 */
export function isInlineAudioShape(media: VoiceNoteShape | null | undefined): boolean {
  return isVoiceNoteShape(media) || isUploadedAudioShape(media)
}
